/* eslint-disable */

import { get, isEqual, uniqBy } from 'lodash';
import { mapActions, mapGetters, mapState } from 'vuex';
import chartsContainer from '@/components/market/market-detail-charts/container/container.vue';
import chartsHeader from '@/components/market/market-detail-charts/header/header.vue';
import chartsTable from '@/components/market/market-detail-charts/table/table.vue';
import chartsTimeline from '@/components/market/market-detail-charts/timeline/timeline.vue';
import marketContractsStats from '@/components/market/market-contracts-stats/market-contracts-stats.vue';
import loaderMixin from '@/mixins/loader';
import { groupByKeepOrder, hasMarketHiddenContracts } from '@/utils/helpers';
import { regularContractPrice } from './utils';

// const YES_CONTRACT_TYPE = 'Yes';
// const NO_CONTRACT_TYPE = 'No';
// const NO_CONTRACT_LINE_COLOR = '#F27130';
const MARKET_SINGLE_CONTRACT_NAME = 'Closing Price';
const INITIAL_SHOW_CONTRACTS = 3;
export const INITIAL_MAX_CONTRACTS = 6;

const CHART_TYPES = {
  LINE: 'line',
  CANDLE: 'candle',
};

export default {
  mixins: [
    loaderMixin,
  ],
  props: {
    market: {
      type: Object,
      required: false,
    },
    chartOptions: {
      type: Object,
      default: null,
    },
  },
  data() {
    const contractIds = get(this.chartOptions, 'contractIds', []) || [];
    return {
      hasMarketHiddenContracts,
      chartType: CHART_TYPES.LINE,
      preparedContractIds: null,
      toggled: false,
      disabledContracts: new Set(),
      chartShowContractsInitial: INITIAL_SHOW_CONTRACTS,
      chartMaxContracts: contractIds.length > 0 ? contractIds.length : INITIAL_MAX_CONTRACTS,
      chartTimeline: null,
      chartDataLoaded: false,
      chartData: [],
      originalChartData: [],
      showStats: false,
    };
  },
  components: {
    chartsContainer,
    chartsHeader,
    chartsTable,
    chartsTimeline,
    marketContractsStats,
  },
  computed: {
    ...mapGetters('app', ['isLayoutDesktop', 'isLayoutMobile']),
    ...mapState('market', ['marketContractsStats']),
    title() {
      return get(this.chartOptions, 'chartTitle', null);
    },
    caption() {
      return get(this.chartOptions, 'chartCaption', null);
    },
    chartContracts() {
      const results = [];
      const contractIdDataGroup = groupByKeepOrder(this.chartData, 'contractId');

      // show contracts that represent selected point on time on the chart
      contractIdDataGroup.forEach((data) => {
        results.push(data[data.length - 1]);
      });
      return results.map((item) => {
        const contract = this.market.contracts.find(i => i.contractId === item.contractId) || {};
        return {
          ...item,
          contractImageUrl: contract.contractImageUrl,
          contractImageSmallUrl: contract.contractImageSmallUrl,
        };
      })
    },
    leftToShowContracts() {
      if (this.market.contracts.length < INITIAL_MAX_CONTRACTS) { return 0; }
      return this.showChartsTable && (this.market.contracts.length - this.chartMaxContracts);
    },
    showChartsTable() {
      const noChartOptionOrLegendOn = this.chartOptions === null || get(this.chartOptions, 'includeLegend', false);
      return this.chartContracts.length > 1 && (this.isLayoutDesktop || this.toggled) && noChartOptionOrLegendOn;
    },
    showChartsKey() {
      return this.chartContracts.length > 1 && (this.isLayoutMobile && !this.toggled) && this.chartOptions === null;
    },
    filteredChartData() {
      return this.chartData.filter(el => el.checked);
    },
  },
  methods: {
    ...mapActions('chart', ['getMarketChartData']),
    ...mapActions('market', [
      'getMarketContractsStats', 'clearMarketContractsStats',
    ]),
    prepareDisabledContracts(data) {
      const results = [];
      const contractIdDataGroup = groupByKeepOrder(data, 'contractId');
      // show contracts that represent selected point on time on the art
      contractIdDataGroup.forEach((groupData) => {
        results.push(groupData[groupData.length - 1]);
      });
      results.forEach((el, index) => {
        if (index >= this.chartShowContractsInitial) {
          this.disabledContracts.add(el.contractId);
        }
      });
    },
    prepareChartData(data) {
      const contracts = uniqBy(data, el => el.contractId);
      const contractIds = contracts.map(el => el.contractId);
      const isYesNoMarket = contracts.length === 1;
      if (!isEqual(contractIds, this.preparedContractIds)) {
        this.prepareDisabledContracts(data);
        this.preparedContractIds = contractIds;
      }

      data.forEach((el) => {
        el.additional = false;
        el.checked = this.checkIsContractEnabled(el);
        el.chartPrice = regularContractPrice(el);

        if (isYesNoMarket) {
          el.contractName = MARKET_SINGLE_CONTRACT_NAME;
        }
      });

      return data;
    },
    checkIsContractEnabled(el) {
      return !this.disabledContracts.has(el.contractId);
    },
    loadStats() {
      this.loaderAdd();
      this.getMarketContractsStats(this.market.marketId)
        .then(() => this.loaderRemove())
        .catch(() => this.loaderRemove());
    },
    loadChartData({ useOriginalData = false }) {
      if (useOriginalData) {
        const data = [...this.originalChartData];
        this.chartData = this.prepareChartData(data);
      } else {
        const params = {
          marketId: this.market.marketId,
          maxContracts: this.chartMaxContracts,
          timespan: this.chartTimeline,
        };
        const storyId = get(this.chartOptions, 'cmsStoryId', null);
        if (storyId) {
          params.storyId = storyId;
          params.chartStoryVersionId = get(this.chartOptions, 'cmsVersionId', null);
          params.chartIndex = get(this.chartOptions, 'chartIndex', null);
          params.sectionType = get(this.chartOptions, 'sectionType', null);
        }

        this.loaderAdd();
        this.getMarketChartData(params)
          .then((data) => {
            this.originalChartData = [...data];
            this.chartData = this.prepareChartData(data);
            this.chartDataLoaded = true;
            this.loaderRemove();
          }).catch(() => this.loaderRemove());
      }
    },
    onShowKey() {
      this.toggled = true;
    },
    onTimelineChange(timeline) {
      if (timeline === 'stats') {
        this.showStats = true;
        this.loadStats();
      } else {
        this.showStats = false;
        this.chartTimeline = timeline;
        this.loadChartData({});
      }
    },
    onTypeChange(type) {
      this.chartType = type;

      if ((type === CHART_TYPES.CANDLE) && this.chartData) {
        const contracts = uniqBy(this.chartData, el => el.contractId);
        const enabled = contracts.find(i => this.checkIsContractEnabled(i)) || contracts[0];
        const contractIds = contracts
          .map(i => i.contractId)
          .filter(i => i !== enabled.contractId);
        this.disabledContracts = new Set(contractIds);
        this.loadChartData({ useOriginalData: true });
      }
    },
    onContractToggle(item, checked) {
      const contractId = item.contractId;
      // const contractsNum = Math.min(this.market.contracts.length, INITIAL_MAX_CONTRACTS);
      // const disabledContractsMaxSize = contractsNum;

      if (this.chartType === CHART_TYPES.CANDLE) {
        if (!checked) {
          this.disabledContracts.add(contractId);
        } else {
          const contractIds = uniqBy(this.chartData, el => el.contractId)
            .map(i => i.contractId)
            .filter(i => i !== contractId);
          this.disabledContracts = new Set(contractIds);
        }
      } else {
        if (!checked) {
          this.disabledContracts.add(contractId);
        } else {
          this.disabledContracts.delete(contractId);
        }
      }

      this.loadChartData({ useOriginalData: true });
    },
  },
  beforeDestroy() {
    this.clearMarketContractsStats();
  },
};



// WEBPACK FOOTER //
// ./src/components/market/market-detail-charts/market-detail-charts.js